<template>
    <v-dialog
        :model-value="modelValue"
        :max-width="560"
        :persistent="submitting"
        data-testid="add-artwork-wizard"
        @update:model-value="onDialogUpdate"
    >
        <div class="aaw-card" role="dialog" aria-modal="true">
            <header class="aaw-header">
                <h3 class="aaw-title">Add artwork</h3>
                <button
                    type="button"
                    class="aaw-close"
                    aria-label="Close"
                    data-testid="aaw-close"
                    :disabled="submitting"
                    @click="onCancel"
                >
                    &times;
                </button>
            </header>

            <form class="aaw-body" @submit.prevent="onSubmit">
                <p class="aaw-help">
                    Upload an artwork file and name it. You'll bind it to a
                    product (and a method) in a separate step.
                </p>

                <!--
                    File picker.

                    We render a native ``<input type="file">`` rather than
                    Vuetify's ``v-file-input`` because:
                    (1) ``v-file-input`` wraps the native input in a layer
                        of Vuetify state that fights jsdom in vitest -- the
                        existing UploadArtworkModal landed on the same
                        native-input pattern for the same reason.
                    (2) We need the raw ``File`` object for the multipart
                        upload contract the artworks store enforces; the
                        native input gives us that with one event handler.
                    The ``accept`` attribute is per
                    ``project_p36-wizard-decisions`` (Jake 2026-06-19).
                -->
                <label class="aaw-field">
                    <span class="aaw-label">
                        Artwork file <span class="aaw-req">*</span>
                    </span>
                    <input
                        ref="fileInput"
                        type="file"
                        class="aaw-input"
                        :accept="ACCEPT_LIST"
                        data-testid="aaw-file"
                        :disabled="submitting"
                        @change="onFileSelected"
                    />
                    <span
                        v-if="form.file"
                        class="aaw-hint"
                        data-testid="aaw-file-name"
                    >
                        Selected: {{ form.file.name }}
                    </span>
                    <span class="aaw-hint">
                        Accepted: {{ ACCEPT_LIST_LABEL }}. Max 50 MB (SVG: 10 MB).
                    </span>
                    <span
                        v-if="fileError"
                        class="aaw-error"
                        data-testid="aaw-file-error"
                    >
                        {{ fileError }}
                    </span>
                </label>

                <!--
                    Companion-DST prompt (PR-4b, Q2 = ENCOURAGE).

                    When the chosen artwork is an .emb (Wilcom design), we
                    ENCOURAGE attaching the machine .dst: with it, the preview
                    renders locally for free; without it, the backend falls
                    back to a paid Wilcom render. Still OPTIONAL -- the user
                    can submit an .emb alone. Shown only for .emb so it never
                    clutters the common raster/vector upload.
                -->
                <label
                    v-if="showCompanionPrompt"
                    class="aaw-field"
                    data-testid="aaw-companion-field"
                >
                    <span class="aaw-label">Machine file (.dst) - optional</span>
                    <input
                        ref="companionInput"
                        type="file"
                        class="aaw-input"
                        accept=".dst"
                        data-testid="aaw-companion-file"
                        :disabled="submitting"
                        @change="onCompanionSelected"
                    />
                    <span
                        v-if="form.companionFile"
                        class="aaw-hint"
                        data-testid="aaw-companion-name"
                    >
                        Selected: {{ form.companionFile.name }}
                    </span>
                    <span class="aaw-hint" data-testid="aaw-companion-help">
                        Have the machine .dst? Attach it to skip the render
                        cost and get a free local preview. Optional - an .emb
                        alone still uploads.
                    </span>
                    <span
                        v-if="companionError"
                        class="aaw-error"
                        data-testid="aaw-companion-error"
                    >
                        {{ companionError }}
                    </span>
                </label>

                <!--
                    Bug Intake #332 (GH #4292): a .dst/.emb a client or a
                    team member already approved before ThreadFlow. Shown
                    only for a stitch file; ticking it asks who.
                -->
                <PriorApprovalAttestation
                    v-model="form.priorApproval"
                    :file-name="form.file ? form.file.name : ''"
                    :distributor-id="distributorId"
                    :disabled="submitting || uploaded"
                />

                <label class="aaw-field">
                    <span class="aaw-label">
                        Name <span class="aaw-req">*</span>
                    </span>
                    <input
                        v-model.trim="form.name"
                        type="text"
                        class="aaw-input"
                        maxlength="255"
                        placeholder="What you'll see in the library"
                        data-testid="aaw-name"
                        :disabled="submitting"
                    />
                    <span
                        v-if="nameError"
                        class="aaw-error"
                        data-testid="aaw-name-error"
                    >
                        {{ nameError }}
                    </span>
                </label>

                <label class="aaw-field">
                    <span class="aaw-label">Description (optional)</span>
                    <textarea
                        v-model="form.description"
                        class="aaw-input"
                        rows="2"
                        maxlength="1000"
                        placeholder="Notes for your team. Optional."
                        data-testid="aaw-description"
                        :disabled="submitting"
                    />
                    <!--
                        BACKEND GAP: ``Artwork`` has no ``description``
                        column today. The wizard captures the value for
                        UX continuity but the artworks store action does
                        NOT forward it. Adding the column is tracked in
                        the PR body. Same pattern the legacy 3-step
                        wizard used for ``clientId`` (captured + dropped).
                    -->
                </label>

                <label class="aaw-field">
                    <span class="aaw-label">Client tag (optional)</span>
                    <input
                        v-model.trim="form.clientTag"
                        type="text"
                        class="aaw-input"
                        maxlength="120"
                        placeholder="Short label, e.g. 'BNSF', 'spring 2026'"
                        data-testid="aaw-client-tag"
                        :disabled="submitting"
                    />
                    <!--
                        BACKEND GAP: ``Artwork`` has no ``client_tag``
                        column today either. Same dropped-on-submit
                        pattern as ``description``.
                    -->
                </label>

                <p
                    v-if="submitError"
                    class="aaw-error aaw-error--submit"
                    data-testid="aaw-submit-error"
                >
                    {{ submitError }}
                </p>

                <DuplicateArtworkNotice
                    v-if="duplicates.length"
                    :duplicates="duplicates"
                    :busy="submitting"
                    @use-existing="onUseExisting"
                    @upload-anyway="onSubmit({ allowDuplicate: true })"
                />

                <footer class="aaw-footer">
                    <button
                        type="button"
                        class="aaw-btn"
                        data-testid="aaw-cancel"
                        :disabled="submitting"
                        @click="onCancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="aaw-btn aaw-btn--primary"
                        data-testid="aaw-submit"
                        :disabled="!canSubmit"
                    >
                        {{ submitting ? 'Uploading...' : 'Upload artwork' }}
                    </button>
                </footer>
            </form>
        </div>
    </v-dialog>
</template>

<script>
/**
 * AddArtworkWizard.vue -- single-step modal that uploads an artwork
 * file + captures lightweight metadata. Lives at the Decoration
 * Library list entry point.
 *
 * Refactor P5 PR-C (Boulders #163 -- https://trello.com/c/S5kN5poU).
 *
 * Pre-refactor: a 3-step ``DecorationAddWizard`` (file + name -> method
 * + locations -> client) was the only entry point. P5 splits that
 * wizard into two flows per the locked product decisions:
 *
 *   - AddArtworkWizard (this component): upload-only, no method, no
 *     placement. Surfaces at the Library list.
 *   - AddPlacementWizard (PR-D, separate): bind an existing Artwork to
 *     a product zone + method. Surfaces at the product editor.
 *
 * The legacy ``DecorationAddWizard`` stays mounted at
 * ``/distributor/decoration-library/new`` until PR-D ships
 * ``AddPlacementWizard`` -- the existing product-editor "+ Add
 * placement" button still routes through it.
 *
 * Upload contract
 * ---------------
 * Delegates the wire-level work to ``useArtworksStore.uploadArtwork()``:
 *   1. multipart POST /api/upload_image -> { uuid }
 *   2. JSON:API POST /api/artworks/ with the persisted filename + image_url
 *   3. fire-and-forget POST /api/artworks/<id>/process_assets for thumbs
 *
 * The store action enforces the repository (passed in as a prop -- the
 * Library list resolves the tenant's first repository via the existing
 * /api/artwork_repositories/mine alias and forwards it here).
 *
 * Backend column gaps (documented in PR body)
 * -------------------------------------------
 * ``description`` and ``clientTag`` are captured in the form but NOT
 * forwarded -- ``Artwork`` has no column for either today. Adding
 * those columns is out of scope for this PR; the wizard surfaces the
 * fields so the UX contract for the eventual extension is already
 * wired. Same precedent the legacy wizard used for ``clientId``.
 *
 * Validation
 * ----------
 *   - file: required. Type must match the shared upload contract
 *     accept-list (see @/utils/artworkUploadContract). Max 50 MB.
 *   - name: required. Maps to ``Artwork.filename`` via the store action
 *     (the store appends the S3 uuid suffix so the file can be
 *     resolved when the Variant<->Artwork FK lands).
 *
 * Test expectations (vitest mount tests, see
 * frontend/src/components/decoration/__tests__/AddArtworkWizard.spec.js):
 *   - Renders when ``modelValue=true``; renders nothing when false.
 *   - Submit disabled until file + name are populated.
 *   - File type allowlist enforced client-side (.docx blocked).
 *   - File size > 50 MB blocked client-side.
 *   - Submit calls the Pinia store action with the expected shape.
 *   - Success emits ``created`` with the new artwork.
 *   - Backend error surfaces inline; modal stays open.
 *   - Cancel closes the modal.
 */
import { useArtworksStore } from '@/stores/artworks'
import PriorApprovalAttestation from '@/components/decoration/PriorApprovalAttestation.vue'
import {
    clearedPriorApproval,
    isPriorApprovalComplete,
    priorApprovalUploadArgs,
} from '@/utils/priorApproval'
import DuplicateArtworkNotice from './DuplicateArtworkNotice.vue'
import {
    ARTWORK_UPLOAD_ACCEPT,
    ARTWORK_UPLOAD_ACCEPT_LABEL,
    extensionOf,
    validateArtworkFile,
} from '@/utils/artworkUploadContract'

/**
 * Accept-list + size cap come from the shared upload contract
 * (frontend/src/utils/artworkUploadContract.js), which mirrors the
 * backend enforcement in api/src/endpoints/upload.py. The wizard's
 * check is a UX hint + early-reject so users don't wait on a 400/413
 * round trip; the backend enforces the same contract authoritatively.
 */
const ACCEPT_LIST = ARTWORK_UPLOAD_ACCEPT
const ACCEPT_LIST_LABEL = ARTWORK_UPLOAD_ACCEPT_LABEL

/**
 * Master switch for the companion machine-file (.dst) prompt shown after an
 * .emb is chosen. OFF (Jake, 2026-10-28): asking for a .dst right after the
 * user uploaded an .emb is confusing. The plumbing below (form.companionFile,
 * onCompanionSelected, the store's companion upload, the backend's companion
 * handling) is intentionally kept dormant, not deleted: with no companion the
 * backend already falls back to the paid Wilcom render. Flip this back on once
 * a native app can supply a same-named .dst from the user's filesystem, which
 * a browser cannot do.
 */
const SHOW_COMPANION_DST_PROMPT = false

export default {
    name: 'AddArtworkWizard',

    components: { DuplicateArtworkNotice, PriorApprovalAttestation },

    props: {
        /**
         * v-model open/close. The parent owns the dialog visibility.
         */
        modelValue: { type: Boolean, default: false },
        /**
         * The tenant's artwork repository id. The parent resolves it
         * once via ``GET /api/artwork_repositories/mine`` and forwards
         * it -- avoids a second round-trip every time the modal opens.
         * When null the wizard surfaces an inline error rather than
         * letting the user submit + 400 against the backend.
         */
        repositoryId: { type: [Number, String], default: null },
        /**
         * The repository's distributor, so the "already approved by my
         * client" picker offers only that tenant's clients (#4292).
         */
        distributorId: { type: [Number, String], default: null },
    },

    emits: ['update:modelValue', 'created', 'reused'],

    data() {
        return {
            ACCEPT_LIST,
            ACCEPT_LIST_LABEL,
            // Instance copy of SHOW_COMPANION_DST_PROMPT so tests can mount
            // with the dormant prompt forced on.
            companionPromptEnabled: SHOW_COMPANION_DST_PROMPT,
            form: {
                file: null,
                name: '',
                description: '',
                clientTag: '',
                companionFile: null,
                priorApproval: clearedPriorApproval(),
            },
            fileError: null,
            nameError: null,
            companionError: null,
            submitError: null,
            // Bug Intake #310: byte-identical artwork the tenant already
            // has, held for the user's choice.
            duplicates: [],
            submitting: false,
            // True once the file is in the library but its prior approval
            // could not be recorded: submitting again would duplicate it.
            uploaded: false,
        }
    },

    computed: {
        canSubmit() {
            return (
                !this.submitting &&
                !this.uploaded &&
                Boolean(this.form.file) &&
                Boolean(this.form.name) &&
                !this.fileError &&
                !this.companionError &&
                isPriorApprovalComplete(this.form.priorApproval)
            )
        },
        /**
         * Show the companion-DST prompt only when the chosen file is an
         * .emb (Wilcom design) -- the one format whose preview otherwise
         * falls to a paid Wilcom render (PR-4b, Q2 = ENCOURAGE). Gated by
         * SHOW_COMPANION_DST_PROMPT, currently off.
         */
        showCompanionPrompt() {
            return (
                this.companionPromptEnabled &&
                Boolean(this.form.file) &&
                extensionOf(this.form.file.name) === '.emb'
            )
        },
    },

    watch: {
        modelValue(open) {
            if (open) this._resetForm()
        },
    },

    methods: {
        _resetForm() {
            this.form = {
                file: null,
                name: '',
                description: '',
                clientTag: '',
                companionFile: null,
                priorApproval: clearedPriorApproval(),
            }
            this.fileError = null
            this.nameError = null
            this.companionError = null
            this.submitError = null
            this.submitting = false
            this.uploaded = false
            this.duplicates = []
            // Clear the native inputs too so re-opening the modal with
            // the same filename re-fires @change.
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
            if (this.$refs.companionInput) {
                this.$refs.companionInput.value = ''
            }
        },

        onFileSelected(evt) {
            this.duplicates = []
            const file =
                evt && evt.target && evt.target.files && evt.target.files[0]
            this.form.file = file || null
            // Pre-fill the name from the filename (sans extension) the
            // first time the user picks a file so they don't have to
            // re-type the obvious. Only auto-fills when name is blank
            // so re-picking a file doesn't clobber a user's edit.
            if (file && !this.form.name) {
                const dot = file.name.lastIndexOf('.')
                this.form.name = dot > 0 ? file.name.slice(0, dot) : file.name
            }
            // If the new primary file is no longer an .emb, drop any
            // previously-attached companion so it can't be sent with a
            // non-embroidery upload (the prompt hides in that case).
            if (!this.showCompanionPrompt) {
                this.form.companionFile = null
                this.companionError = null
                if (this.$refs.companionInput) {
                    this.$refs.companionInput.value = ''
                }
            }
            this._validateFile()
        },

        onCompanionSelected(evt) {
            const file =
                evt && evt.target && evt.target.files && evt.target.files[0]
            this.form.companionFile = file || null
            // The companion is OPTIONAL, so an empty selection is fine.
            // When one IS chosen, it must be a .dst (a machine stitch file);
            // anything else is a user mistake we surface inline.
            if (file && extensionOf(file.name) !== '.dst') {
                this.companionError =
                    'The machine file must be a .dst. Leave it empty to skip.'
            } else {
                this.companionError = null
            }
        },

        _validateFile() {
            const check = validateArtworkFile(this.form.file)
            this.fileError = check.error
            return check.ok
        },

        _validateName() {
            if (!this.form.name) {
                this.nameError = 'Give this artwork a name.'
                return false
            }
            this.nameError = null
            return true
        },

        _validate() {
            // Order matters: validate both so the user sees every
            // error at once, not one per submit attempt.
            const okFile = this._validateFile()
            const okName = this._validateName()
            return okFile && okName
        },

        async onSubmit({ allowDuplicate = false } = {}) {
            if (!this._validate()) return
            if (!this.repositoryId) {
                this.submitError =
                    'No artwork repository is available for this tenant. ' +
                    'Contact platform support.'
                return
            }
            this.submitting = true
            this.submitError = null
            this.duplicates = []
            try {
                const store = useArtworksStore()
                const created = await store.uploadArtwork({
                    // Bug Intake #310: hold a byte-identical re-upload and
                    // offer the existing artwork instead of forking a copy.
                    checkDuplicates: true,
                    allowDuplicate,
                    file: this.form.file,
                    filename: this.form.name,
                    repositoryId: this.repositoryId,
                    // Companion machine .dst (PR-4b) -- only meaningful for an
                    // .emb; the store ignores it for other formats. Passed
                    // only when the prompt is showing AND a file is attached.
                    companionFile: this.showCompanionPrompt
                        ? this.form.companionFile
                        : null,
                    // Bug Intake #332: the client or team member who
                    // already approved this stitch file, when ticked.
                    ...priorApprovalUploadArgs(this.form.priorApproval),
                })
                this.$emit('created', created)
                this.$emit('update:modelValue', false)
            } catch (err) {
                if (err && err.kind === 'duplicate') {
                    this.duplicates = err.duplicates
                    return
                }
                // The file uploaded but its prior approval was refused:
                // tell the library about the new artwork, keep the dialog
                // open on the explanation, and do not offer a re-upload.
                if (err?.uploadedArtwork) {
                    this.uploaded = true
                    this.submitError = err.message
                    this.$emit('created', err.uploadedArtwork)
                    return
                }
                // Prefer the SERVER's explanation. axios sets err.message to
                // its own client-side wording (e.g. "timeout exceeded" for
                // ECONNABORTED), which is actively misleading when the server
                // in fact answered fast with a 4xx -- the upload endpoint
                // returns { error: "..." } and that is the reason a user can
                // act on.
                const serverError =
                    err?.response?.data?.error ||
                    err?.response?.data?.message
                this.submitError =
                    serverError ||
                    (err && err.message) ||
                    'Upload failed. Try again.'
            } finally {
                this.submitting = false
            }
        },

        onUseExisting(existing) {
            this.$emit('reused', existing)
            this.$emit('update:modelValue', false)
        },

        onCancel() {
            if (this.submitting) return
            this.$emit('update:modelValue', false)
        },

        onDialogUpdate(open) {
            // v-dialog emits update:model-value on backdrop click /
            // ESC. Forward it to the parent unless we're mid-upload
            // (the dialog is also rendered ``:persistent`` then, but
            // belt-and-suspenders).
            if (this.submitting) return
            this.$emit('update:modelValue', open)
        },
    },
}
</script>

<style scoped>
.aaw-card {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
    max-height: 92vh;
    overflow-y: auto;
}

.aaw-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.aaw-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.aaw-close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
}

.aaw-close:disabled {
    cursor: not-allowed;
    opacity: 0.4;
}

.aaw-body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.aaw-help {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
    margin: 0 0 4px 0;
}

.aaw-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.aaw-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    font-weight: 500;
}

.aaw-req {
    color: #d32f2f;
    margin-left: 2px;
}

.aaw-input {
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
}

.aaw-input:disabled {
    background: rgba(0, 0, 0, 0.03);
    cursor: not-allowed;
}

.aaw-hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.aaw-error {
    color: #d32f2f;
    font-size: 12px;
    margin: 0;
}

.aaw-error--submit {
    margin-top: 4px;
    font-size: 14px;
}

.aaw-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-top: 4px;
}

.aaw-btn {
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    cursor: pointer;
    font-size: 14px;
}

.aaw-btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}

.aaw-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
